import BaseAPI from './base.api';
import type { GetProductionReport } from '../types/fuelAnalytics';

export default class ProductionAPI extends BaseAPI {
  getTotalFleet = async (
    range: string,
    startDate: string,
    endDate: string,
    shouldSendDate: boolean,
    aggregation: string,
    shiftWiseAnalysis: boolean = false,
    shiftNumber: number = 1,
    signal?: AbortSignal
  ) => {
    let params: any = {
      range,
    };
    if (shouldSendDate) {
      params = {
        startDate,
        endDate,
      };
    }
    const response = await this.API.get('production/total-fleet', {
      params: {
        ...params,
        aggregation,
        shiftWiseAnalysis,
        shiftNumber,
      },
      signal,
    });
    return response.data;
  };

  getTotalFleetSummary = async (
    range: string,
    startDate: string,
    endDate: string,
    shouldSendDate: boolean,
    aggregation: string,
    shiftWiseAnalysis: boolean = false,
    shiftNumber: number = 1,
    signal?: AbortSignal
  ) => {
    let params: any = {
      range,
    };
    if (shouldSendDate) {
      params = {
        startDate,
        endDate,
      };
    }
    const response = await this.API.get('production/date-range-summary', {
      params: {
        ...params,
        aggregation,
        shiftWiseAnalysis,
        shiftNumber,
      },
      signal,
    });
    return response.data;
  };

  getTotalFleetProduction = async (
    assetType: string | 'all' | 'hauler' | 'loader',
    shiftWiseAnalysis: boolean = false,
    shiftNumber: number = 1,
    signal?: AbortSignal
  ) => {
    const response = await this.API.get('production/ytd-summary', {
      params: {
        assetType,
        shiftWiseAnalysis,
        shiftNumber,
      },
      signal,
    });
    return response.data;
  };

  getFleetPeriodSummary = async (
    shiftWiseAnalysis: boolean = false,
    shiftNumber: number = 1,
    signal?: AbortSignal
  ) => {
    const response = await this.API.get('production/fleet-period-summary', {
      params: {
        shiftWiseAnalysis,
        shiftNumber,
      },
      signal,
    });
    return response.data;
  };

  getSuggestions = async (
    range: string,
    startDate: string,
    endDate: string,
    shouldSendDate: boolean,
    aggregation: string,
    shiftWiseAnalysis: boolean = false,
    shiftNumber: number = 1,
    signal?: AbortSignal
  ) => {
    let params: any = {
      range,
    };
    if (shouldSendDate) {
      params = {
        startDate,
        endDate,
      };
    }

    const response = await this.API.get('production/suggestions', {
      params: {
        ...params,
        aggregation,
        shiftWiseAnalysis,
        shiftNumber,
      },
      signal,
    });
    return response.data;
  };

  getProductionLoaders = async (
    range: string,
    startDate: string,
    endDate: string,
    shouldSendDate: boolean,
    aggregation: string,
    shiftWiseAnalysis: boolean = false,
    shiftNumber: number = 1,
    signal?: AbortSignal
  ) => {
    let params: any = {
      range,
    };
    if (shouldSendDate) {
      params = {
        startDate,
        endDate,
      };
    }
    const response = await this.API.get('production/production-loaders', {
      params: {
        ...params,
        aggregation,
        shiftWiseAnalysis,
        shiftNumber,
      },
      signal,
    });
    return response.data;
  };

  getProductionReport = async (
    params: GetProductionReport
  ): Promise<ArrayBuffer> => {
    const {
      startDateVal,
      endDateVal,
      range,
      aggregation,
      shiftWiseAnalysis,
      shiftNumber,
      assetType,
      utilizationType,
      returnType,
      shiftHours,
      suggestionData,
    } = params;
    let requestParams: {
      range?: string;
      startDateVal?: string;
      endDateVal?: string;
    } = {
      range,
    };
    if (range === 'Custom' || range === 'Today') {
      requestParams = {
        startDateVal,
        endDateVal,
      };
    }
    const response = await this.API.get('production/reports', {
      params: {
        ...requestParams,
        aggregation,
        range,
        shiftWiseAnalysis: shiftWiseAnalysis ?? false,
        shiftNumber: shiftNumber ?? 1,
        shiftHours: shiftHours
          ? `${shiftHours.start} - ${shiftHours.end}`
          : undefined,
        assetType,
        utilizationType,
        returnType,
        suggestionData: suggestionData
          ? JSON.stringify(suggestionData)
          : undefined,
      },
      responseType: 'arraybuffer',
      headers: {
        Accept:
          returnType === 'excel'
            ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            : 'application/pdf',
      },
    });
    return response.data;
  };
}
